import { supabase } from './supabase'
import { activeOrgId, activeOrgIsOwned } from './activeOrg'
import { getVisibility, setVisibility } from './courseVisibility'
import type { Course, Module, Slide } from '@/types'

// ─── Local course registry (always reliable, works without Supabase) ──────────

const LOCAL_COURSES_KEY = 'learnbee_courses'

function getLocalCourses(): Course[] {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_COURSES_KEY) ?? '[]') as Course[]
  } catch { return [] }
}

function saveLocalCourses(courses: Course[]) {
  localStorage.setItem(LOCAL_COURSES_KEY, JSON.stringify(courses))
}

function addLocalCourse(course: Course) {
  const existing = getLocalCourses().filter(c => c.id !== course.id)
  saveLocalCourses([course, ...existing])
}

function removeLocalCourse(id: string) {
  saveLocalCourses(getLocalCourses().filter(c => c.id !== id))
}

/** Synchronous lookup of a single course by id from the local courses index.
 *  The Library page populates this from Supabase on every load (see getCourses
 *  → saveLocalCourses(merged)), so it's the right place for EditorPage /
 *  PlayerPage to read course metadata (title, language, defaultVoiceGender)
 *  before they have time to do an async Supabase fetch. */
export function getCourseFromIndex(id: string): Course | null {
  return getLocalCourses().find(c => c.id === id) ?? null
}

// ─── Courses ──────────────────────────────────────────────────────────────────

/**
 * List courses. Pass `ownerId` to scope to a single creator — this is the
 * caller's OWN courses. Always pass it for "my courses" surfaces: now that
 * Library courses are cross-user readable (RLS), an unscoped `select('*')`
 * returns other people's Library courses too, which would leak into the
 * user's own list AND get persisted into their local registry. Scoping keeps
 * the registry clean (it's rewritten from the scoped result).
 */
export async function getCourses(ownerId?: string | null): Promise<Course[]> {
  // Organisations (dev only): with an active organisation, "my courses" means that
  // organisation's courses — RLS decides which of them this person may see.
  // Without one (production), exactly as before: the courses this person created.
  const org = activeOrgId()
  // In the organisation you OWN, your courses from before organisations (org_id
  // null) are listed too — where adoption will move them. Nothing is moved here.
  const withPersonal = !!org && activeOrgIsOwned() && !!ownerId
  // Baseline local courses, scoped to the owner when one is given.
  const local = getLocalCourses()
  const localScoped = org
    ? local.filter(c => c.org_id === org || (withPersonal && !c.org_id && c.created_by === ownerId))
    : ownerId ? local.filter(c => c.created_by === ownerId) : local

  try {
    // Courses this user OWNS. We scope by created_by explicitly (NOT just RLS) because the
    // curated Library adds a cross-user read policy on `courses`; relying on open RLS would
    // leak public library courses into everyone's personal list.
    let ownedQuery = supabase
      .from('courses')
      .select('*')
      .order('created_at', { ascending: false })
    if (org && withPersonal) ownedQuery = ownedQuery.or(`org_id.eq.${org},and(org_id.is.null,created_by.eq.${ownerId})`)
    else if (org) ownedQuery = ownedQuery.eq('org_id', org)
    else if (ownerId) ownedQuery = ownedQuery.eq('created_by', ownerId)
    const { data, error } = await ownedQuery
    if (error) throw error

    let remote = (data ?? []) as Course[]

    // Courses explicitly SHARED with this user as a collaborator (never the public library).
    // Looked up via course_collaborators, then fetched by id — so only intentional shares
    // appear. Returns nothing when collaboration RLS is off, which is fine.
    if (ownerId) {
      const { data: collabRows } = await supabase
        .from('course_collaborators')
        .select('course_id')
        .eq('user_id', ownerId)
        .eq('status', 'active')
      const ids = (collabRows ?? []).map(r => r.course_id).filter(Boolean)
      if (ids.length) {
        const { data: shared } = await supabase.from('courses').select('*').in('id', ids)
        const ownedIds = new Set(remote.map(c => c.id))
        remote = [...remote, ...((shared ?? []) as Course[]).filter(c => !ownedIds.has(c.id))]
      }
    }

    // Merge: remote is source of truth, add any local-only courses — but not inside an organisation
    // (21 Oct 2026): there every course lives on the server, and a course deleted on another device
    // (or whose delete raced) came back here as a ghost tile that could be opened and edited.
    const remoteIds = new Set(remote.map(c => c.id))
    const localOnly = org ? [] : localScoped.filter(c => !remoteIds.has(c.id))
    const merged = [...remote, ...localOnly]

    // Sync back to local registry. When scoped, this also evicts any
    // cross-user courses that may have leaked in before. Skipped when the
    // organisation changed while this request was in flight — a slow answer
    // for the previous organisation must not overwrite the current one's list.
    if (activeOrgId() === org) saveLocalCourses(merged)
    return merged
  } catch {
    // Supabase unavailable — return local courses
    return localScoped
  }
}

export async function createCourse(
  title: string,
  description: string | undefined,
  thumbnail_url: string | undefined,
  language: string,
  createdBy: string,
  /** Course settings from a course template; unset keys keep the defaults below. */
  settings?: Partial<Pick<Course, 'pass_mark' | 'min_questions' | 'default_voice_gender' | 'default_narration_skippable' | 'default_auto_advance' | 'default_auto_advance_segments' | 'font_family' | 'narration_seconds' | 'custom_slide_theme'>>,
): Promise<Course> {
  const id = `course-${Date.now()}`
  const now = new Date().toISOString()
  const course: Course = {
    id,
    // The active organisation (Organisations, dev only); null in production, as before.
    org_id: activeOrgId(),
    created_by: createdBy,
    title,
    description,
    thumbnail_url,
    language,
    is_root: true,
    status: 'draft',
    pass_mark: 80,
    min_questions: 5,
    default_voice_gender: 'female',
    share_code: null,
    // Interactivity defaults — applied as seeds when new slides are created
    // in this course. Safe defaults are all false (today's behaviour: audio
    // plays through, no per-element gating, no auto-advance).
    default_narration_skippable: false,
    default_interact_per_element: false,
    default_auto_advance: false,
    default_auto_advance_segments: false,
    font_family: 'inter',
    narration_seconds: 15,
    custom_slide_theme: { mode: 'light', colors: [] },
    ...settings,
    created_at: now,
    updated_at: now,
  }

  // Cache locally so the UI is responsive even if the network is slow.
  addLocalCourse(course)

  // Persist to Supabase. Errors are loud now — we want to know if RLS or
  // anything else rejects the write rather than silently using localStorage
  // as the primary store.
  const { error } = await supabase.from('courses').insert({
    id: course.id,
    org_id: course.org_id,
    created_by: course.created_by,
    title: course.title,
    description: course.description,
    thumbnail_url: course.thumbnail_url,
    language: course.language,
    is_root: course.is_root,
    status: course.status,
    pass_mark: course.pass_mark,
    min_questions: course.min_questions,
    default_voice_gender: course.default_voice_gender,
    share_code: course.share_code,
    default_narration_skippable: course.default_narration_skippable,
    default_interact_per_element: course.default_interact_per_element,
    default_auto_advance: course.default_auto_advance,
    default_auto_advance_segments: course.default_auto_advance_segments,
    font_family: course.font_family,
    narration_seconds: course.narration_seconds,
    custom_slide_theme: course.custom_slide_theme,
    created_at: course.created_at,
    updated_at: course.updated_at,
  })
  if (error) {
    console.error('[Supabase] createCourse failed:', error.message, error)
    // Don't throw — the local cache means the user's UI keeps working.
    // But the error is now visible in DevTools so we'll catch regressions early.
  }

  return course
}

/**
 * Create a translated child course — a NEW course row linked back to its root
 * via root_course_id, with is_root=false and the target language. Title /
 * description arrive already translated from the translate engine. Mirrors
 * createCourse's insert shape but flips the lineage fields.
 */
export async function createTranslatedCourse(params: {
  rootId: string
  /** The original's organisation (null = personal). Defaults to the active one. */
  orgId?: string | null
  createdBy: string
  title: string
  description?: string
  thumbnail_url?: string
  language: string
  /** Pre-generated id (so audio can be cached under it before the row is saved). */
  id?: string
  default_voice_gender?: 'female' | 'male'
  pass_mark?: number
  min_questions?: number
  default_narration_skippable?: boolean
  default_auto_advance?: boolean
  default_auto_advance_segments?: boolean
  // The rest of the original's course settings (12 Oct 2026) — a translation behaves like it.
  default_interact_per_element?: boolean
  font_family?: string
  narration_seconds?: number
  custom_slide_theme?: Course['custom_slide_theme']
}): Promise<Course> {
  const id = params.id ?? `course-${Date.now()}`
  const now = new Date().toISOString()
  const course: Course = {
    id,
    // A translation lives where its original does.
    org_id: params.orgId !== undefined ? params.orgId : activeOrgId(),
    created_by: params.createdBy,
    title: params.title,
    description: params.description,
    thumbnail_url: params.thumbnail_url,
    language: params.language,
    is_root: false,
    root_course_id: params.rootId,
    status: 'draft',
    pass_mark: params.pass_mark ?? 80,
    min_questions: params.min_questions ?? 5,
    default_voice_gender: params.default_voice_gender ?? 'female',
    share_code: null,
    default_narration_skippable: params.default_narration_skippable ?? false,
    default_interact_per_element: params.default_interact_per_element ?? false,
    default_auto_advance: params.default_auto_advance ?? false,
    default_auto_advance_segments: params.default_auto_advance_segments ?? false,
    font_family: params.font_family,
    narration_seconds: params.narration_seconds,
    custom_slide_theme: params.custom_slide_theme,
    created_at: now,
    updated_at: now,
  }

  addLocalCourse(course)

  const { error } = await supabase.from('courses').insert({
    id: course.id,
    org_id: course.org_id,
    created_by: course.created_by,
    title: course.title,
    description: course.description,
    thumbnail_url: course.thumbnail_url,
    language: course.language,
    is_root: course.is_root,
    root_course_id: course.root_course_id,
    status: course.status,
    pass_mark: course.pass_mark,
    min_questions: course.min_questions,
    default_voice_gender: course.default_voice_gender,
    share_code: course.share_code,
    default_narration_skippable: course.default_narration_skippable,
    default_interact_per_element: course.default_interact_per_element,
    default_auto_advance: course.default_auto_advance,
    default_auto_advance_segments: course.default_auto_advance_segments,
    // Left out when the original has none, so the column defaults apply as they do for a new course.
    ...(course.font_family ? { font_family: course.font_family } : {}),
    ...(course.narration_seconds != null ? { narration_seconds: course.narration_seconds } : {}),
    ...(course.custom_slide_theme ? { custom_slide_theme: course.custom_slide_theme } : {}),
    created_at: course.created_at,
    updated_at: course.updated_at,
  })
  if (error) console.error('[Supabase] createTranslatedCourse failed:', error.message, error)

  // Organisation courses: a translation of a SHARED course starts shared too
  // (06_team_courses.sql) — the team sees every language, not just the original.
  if (!error && course.org_id?.startsWith('org_')) {
    try {
      const vis = await getVisibility([params.rootId])
      if (vis.get(params.rootId) === 'org') await setVisibility(course.id, 'org', params.createdBy)
    } catch (e) {
      console.warn('[visibility] could not share the new translation:', (e as Error).message)
    }
  }

  return course
}

/**
 * Group courses into translation families. The group key is
 * `root_course_id ?? id`, so a root and all its translations land together.
 * `root` is the is_root course when present (else the first member as a
 * fallback); `translations` excludes the root.
 */
export interface CourseGroup {
  root: Course
  translations: Course[]
  /** All members including the root, ordered root-first. */
  all: Course[]
}

export function groupByRoot(courses: Course[]): CourseGroup[] {
  const byKey = new Map<string, Course[]>()
  for (const c of courses) {
    const key = c.root_course_id ?? c.id
    const arr = byKey.get(key) ?? []
    arr.push(c)
    byKey.set(key, arr)
  }
  return Array.from(byKey.values()).map(members => {
    const root = members.find(c => c.is_root) ?? members[0]
    const translations = members.filter(c => c.id !== root.id)
    return { root, translations, all: [root, ...translations] }
  })
}

/** Load a single course by id — local registry first, Supabase fallback. */
export async function getCourse(id: string): Promise<Course | null> {
  const local = getLocalCourses().find(c => c.id === id)
  if (local) return local
  try {
    const { data, error } = await supabase.from('courses').select('*').eq('id', id).single()
    if (error) throw error
    return data as Course
  } catch {
    return null
  }
}

/** The course settings a language version shares with its family (12 Oct 2026). */
export type SharedCourseSettings = Partial<Pick<Course, 'default_voice_gender' | 'default_narration_skippable' | 'default_auto_advance'
  | 'default_auto_advance_segments' | 'font_family' | 'narration_seconds' | 'custom_slide_theme' | 'pass_mark' | 'min_questions' | 'thumbnail_url'>>

/** The other language versions of a course (root and translations, not itself). */
export async function otherLanguageVersions(courseId: string): Promise<Array<{ id: string; language: string; title: string }>> {
  const mine = await supabase.from('courses').select('root_course_id').eq('id', courseId).maybeSingle()
  const rootId = (mine.data as { root_course_id?: string | null } | null)?.root_course_id ?? courseId
  const { data } = await supabase.from('courses').select('id, language, title').or(`id.eq.${rootId},root_course_id.eq.${rootId}`)
  return ((data ?? []) as Array<{ id: string; language: string; title: string }>).filter(c => c.id !== courseId)
}

/**
 * Apply course settings to other language versions too (“All languages”). Awaited, so the editor can
 * say how it went; the number that failed comes back. Each version's editor cache is patched so its
 * preview agrees before it is next opened (its sync stamp no longer matches, so the editor reloads).
 */
export async function applySettingsToVersions(ids: string[], updates: SharedCourseSettings): Promise<number> {
  const stamp = new Date().toISOString()
  let failed = 0
  for (const id of ids) {
    const { error } = await supabase.from('courses').update({ ...updates, updated_at: stamp }).eq('id', id)
    if (error) { failed++; console.error('[Supabase] applySettingsToVersions failed:', error.message); continue }
    saveLocalCourses(getLocalCourses().map(c => (c.id === id ? { ...c, ...updates, updated_at: stamp } : c)))
    try {
      const raw = localStorage.getItem(`learnbee_editor_${id}`)
      if (raw) {
        const d = JSON.parse(raw) as Record<string, unknown>
        if (updates.default_narration_skippable !== undefined) d.narrationSkippable = updates.default_narration_skippable
        if (updates.default_voice_gender !== undefined) d.defaultVoiceGender = updates.default_voice_gender
        if (updates.default_auto_advance !== undefined) d.defaultAutoAdvance = updates.default_auto_advance
        if (updates.default_auto_advance_segments !== undefined) d.defaultAutoAdvanceSegments = updates.default_auto_advance_segments
        if (updates.font_family !== undefined) d.fontFamily = updates.font_family
        localStorage.setItem(`learnbee_editor_${id}`, JSON.stringify(d))
      }
    } catch { /* the cache is only a speed-up */ }
  }
  return failed
}

export function updateCourse(
  id: string,
  updates: Partial<Pick<Course, 'title' | 'description' | 'status' | 'thumbnail_url' | 'default_voice_gender' | 'share_code' | 'pass_mark' | 'min_questions' | 'default_narration_skippable' | 'default_interact_per_element' | 'default_auto_advance' | 'default_auto_advance_segments' | 'font_family' | 'narration_seconds' | 'custom_slide_theme'>>,
): string {
  const stamp = new Date().toISOString()
  // Update locally
  const courses = getLocalCourses().map(c =>
    c.id === id ? { ...c, ...updates, updated_at: stamp } : c
  )
  saveLocalCourses(courses)

  // Persist to Supabase
  supabase.from('courses')
    .update({ ...updates, updated_at: stamp })
    .eq('id', id)
    .then(({ error }) => { if (error) console.error('[Supabase] updateCourse failed:', error.message, error) })

  // Returned so callers can record it as their last-known sync stamp
  // (EditorPage freshness check — see getRemoteCourseStamp).
  return stamp
}

/** courses.updated_at straight from Supabase — bypasses the local registry
 *  on purpose. Used by EditorPage to decide whether its localStorage copy is
 *  still authoritative: stamp equals the one we last wrote/loaded → local
 *  wins (covers unsaved offline work); differs → someone else (another
 *  device or a collaborator) wrote since → load fresh. */
export async function getRemoteCourseStamp(id: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('courses').select('updated_at').eq('id', id).single()
    if (error) throw error
    return (data as { updated_at: string | null }).updated_at ?? null
  } catch {
    return null   // offline / unreachable → caller keeps local copy
  }
}

export async function deleteCourse(id: string) {
  removeLocalCourse(id)
  // Also remove editor cache
  localStorage.removeItem(`learnbee_editor_${id}`)

  supabase.from('courses').delete().eq('id', id)
    .then(({ error }) => { if (error) console.error('[Supabase] deleteCourse failed:', error.message, error) })
}

// ─── Modules + slides ─────────────────────────────────────────────────────────

export async function getCourseModules(courseId: string): Promise<Module[]> {
  try {
    const { data: modulesData, error: modErr } = await supabase
      .from('modules')
      .select('*')
      .eq('course_id', courseId)
      .order('position')
    if (modErr) throw modErr
    if (!modulesData || modulesData.length === 0) return []

    const moduleIds = modulesData.map(m => m.id)
    const { data: slidesData, error: slideErr } = await supabase
      .from('slides')
      .select('*')
      .in('module_id', moduleIds)
      .order('position')
    if (slideErr) throw slideErr

    return modulesData.map(m => ({
      id: m.id,
      course_id: courseId,
      title: m.title,
      position: m.position,
      is_opening: m.is_opening ?? false,
      created_at: m.created_at,
      slides: (slidesData ?? [])
        .filter(s => s.module_id === m.id)
        .map(s => ({
          id: s.id,
          module_id: s.module_id,
          format_id: s.format_id,
          position: s.position,
          content: s.content,
          created_at: s.created_at,
          updated_at: s.updated_at,
        } as Slide)),
    })) as Module[]
  } catch {
    return []
  }
}

export async function saveCourseModules(
  courseId: string,
  modules: Module[],
  createdBy: string,
): Promise<string | null> {
  if (modules.length === 0) return null

  try {
    // Never let a save wipe a course (21 Oct 2026 — Greeting Customers lost every slide). This save
    // deletes whatever it doesn't carry, so refuse one that carries another course's modules, or
    // that shares no module with what the course has now (a stale or foreign copy, not an edit).
    const foreign = modules.find(m => m.course_id && m.course_id !== courseId)
    if (foreign) throw new Error(`refused: module ${foreign.id} belongs to course ${foreign.course_id}, not ${courseId}`)
    const { data: current, error: curErr } = await supabase.from('modules').select('id').eq('course_id', courseId)
    if (curErr) throw curErr
    const currentIds = new Set((current ?? []).map(r => (r as { id: string }).id))
    if (currentIds.size > 0 && !modules.some(m => currentIds.has(m.id))) {
      throw new Error(`refused: none of the ${modules.length} modules being saved are this course's ${currentIds.size} — it would replace them all`)
    }

    const { error: modErr } = await supabase.from('modules').upsert(
      modules.map((m, i) => ({
        id: m.id,
        course_id: courseId,
        title: m.title,
        position: i,
        is_opening: m.is_opening ?? false,
        created_at: m.created_at,
        created_by: createdBy,
      })),
      { onConflict: 'id' }
    )
    if (modErr) throw modErr

    const allSlides = modules.flatMap(m =>
      (m.slides ?? []).map((s, sIdx) => ({
        id: s.id,
        module_id: m.id,
        format_id: s.format_id,
        position: sIdx,
        content: s.content,
        created_at: s.created_at,
        updated_at: new Date().toISOString(),
        created_by: createdBy,
      }))
    )
    if (allSlides.length > 0) {
      const { error: slideErr } = await supabase
        .from('slides')
        .upsert(allSlides, { onConflict: 'id' })
      if (slideErr) throw slideErr
    }

    // Delete slides that have been removed from their modules
    // (upsert only adds/updates — we need an explicit delete for removed slides)
    const keepSlideIds = allSlides.map(s => s.id)
    const keepModuleIds = modules.map(m => m.id)
    const slideDeleteQuery = supabase
      .from('slides')
      .delete()
      .in('module_id', keepModuleIds)
    // PostgREST's `not.in.(...)` filter expects plain comma-separated values
    // (no surrounding quotes per item). Wrapping each id in single quotes makes
    // PostgREST treat them as literal strings WITH apostrophes — nothing matches,
    // and the cleanup ends up deleting the rows we just upserted.
    const slideDeleteResult = keepSlideIds.length > 0
      ? await slideDeleteQuery.not('id', 'in', `(${keepSlideIds.join(',')})`)
      : await slideDeleteQuery
    if (slideDeleteResult.error) console.warn('Slide cleanup warning:', slideDeleteResult.error.message)

    // Delete modules that have been removed from this course
    await supabase
      .from('modules')
      .delete()
      .eq('course_id', courseId)
      .not('id', 'in', `(${keepModuleIds.join(',')})`)

    // Touch the course row so every content save moves courses.updated_at —
    // the freshness signal other devices/collaborators compare against.
    const stamp = new Date().toISOString()
    await supabase.from('courses').update({ updated_at: stamp }).eq('id', courseId)
    return stamp
  } catch (err) {
    // Supabase errors are plain objects, not Error instances — stringify so
    // they render usefully in the console instead of "Object".
    const detail = err instanceof Error
      ? err.message
      : (typeof err === 'object' && err !== null)
        ? JSON.stringify(err, Object.getOwnPropertyNames(err))
        : String(err)
    console.error('[Supabase] saveCourseModules failed (local copy is safe):', detail, err)
    return null
  }
}
